// src/components/MandateTaxonomyTags.js — a mandate's taxonomy tags (below
// the Funds / Direct Deals split) as labels. Tags arrive already shaped and
// ordered by the API (src/lib/mandateTaxonomy.js): what it is, the fund's
// sector focus, how, where, stage, size. Shared by the CRM and the portal.

// One colour per kind of tag so a row reads at a glance.
const GROUP_BADGE = {
  what:  'badge-blue',
  focus: 'badge-gray',
  how:   'badge-purple',
  where: 'badge-green',
  stage: 'badge-yellow',
  size:  'badge-gray',
}

const GROUP_TITLE = {
  what: 'Sector / asset class', focus: 'Sector focus', how: 'Structure',
  where: 'Geography', stage: 'Stage', size: 'Check size',
}

// Order of rows in the full view.
const GROUP_ORDER = ['what', 'focus', 'how', 'where', 'stage', 'size']

// Full view: one row per kind of tag, and within a row each parent shown
// once with its children after it ("Private Equity › Buyout - Middle
// Market"; "Technology & Software › Cybersecurity, SaaS"). Reads top-down
// the way the taxonomy descends.
function GroupedTags({ tags, style }) {
  const rows = GROUP_ORDER
    .map(group => {
      const items = tags.filter(t => t.group === group)
      if (!items.length) return null
      // Chains keyed by parent; a tag with no parent is its own chain.
      const chains = []
      for (const t of items) {
        const key = t.parent_code ?? t.code
        let chain = chains.find(c => c.key === key)
        if (!chain) chains.push(chain = { key, parent: t.parent_label, children: [] })
        chain.children.push(t)
      }
      return { group, chains }
    })
    .filter(Boolean)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '8px 14px', alignItems: 'baseline', ...style }}>
      {rows.map(r => [
        <div key={`${r.group}-label`} className="caps-label" style={{ fontSize: 11, fontWeight: 600,
          color: 'var(--text-muted)' }}>
          {GROUP_TITLE[r.group]}
        </div>,
        <div key={`${r.group}-tags`} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {r.chains.map(c => (
            <span key={c.key} className={`badge ${GROUP_BADGE[r.group] ?? 'badge-gray'}`}
              style={{ fontSize: 12, whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.35 }}>
              {c.parent ? `${c.parent} › ` : ''}{c.children.map(t => t.label).join(', ')}
            </span>
          ))}
        </div>,
      ])}
    </div>
  )
}

// compact: tile view. Deepest labels only (chain in the tooltip), at most
// `max` tags with a "+N" for the rest. Full view groups the chains.
export default function MandateTaxonomyTags({ tags, compact = false, max = 5, style }) {
  if (!tags?.length) return null
  if (!compact) return <GroupedTags tags={tags} style={style} />
  const shown = tags.slice(0, max)
  const hidden = tags.length - shown.length

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, ...style }}>
      {shown.map(t => {
        const full = t.parent_label ? `${t.parent_label} › ${t.label}` : t.label
        return (
          <span
            key={t.code}
            className={`badge ${GROUP_BADGE[t.group] ?? 'badge-gray'}`}
            style={{ fontSize: 11, whiteSpace: 'normal', textAlign: 'left', lineHeight: 1.35 }}
            title={`${GROUP_TITLE[t.group] ?? t.dimension}: ${full}`}
          >
            {t.label}
          </span>
        )
      })}
      {hidden > 0 && (
        <span className="badge badge-gray" style={{ fontSize: 11 }}
          title={tags.slice(max).map(t => t.label).join(', ')}>
          +{hidden}
        </span>
      )}
    </div>
  )
}
